
// Compatibility wrapper for react-bottom-scroll-listener
import React, { useMemo, useRef, useCallback, useEffect } from 'react';

// Simple nullish coalescing polyfill
const nullishCoalesce = (value, fallback) => value != null ? value : fallback;

export const useBottomScrollListener = (callback, options = {}) => {
    const {
        offset = 0,
        triggerOnNoScroll = false,
        debounce = 200,
        debounceOptions = { leading: true }
    } = options;

    const scrollRef = useRef(null);

    const handleScroll = useCallback(() => {
        const element = scrollRef.current || document.scrollingElement || document.documentElement;
        
        if (element) {
            const scrollTop = element.scrollTop;
            const scrollHeight = element.scrollHeight;
            const clientHeight = element === document.scrollingElement || element === document.documentElement 
                ? window.innerHeight 
                : element.clientHeight;
            
            if (Math.round(scrollTop + clientHeight) >= Math.round(scrollHeight - offset)) {
                callback();
            }
        }
    }, [callback, offset]);

    useEffect(() => {
        const element = scrollRef.current;
        const target = element || window;
        const eventTarget = element || document;
        
        eventTarget.addEventListener('scroll', handleScroll);
        
        if (triggerOnNoScroll) {
            handleScroll();
        }
        
        return () => {
            eventTarget.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll, triggerOnNoScroll]);

    return scrollRef;
};

export const BottomScrollListener = ({ children, onBottom, offset, debounce, debounceOptions, triggerOnNoScroll }) => {
    const ref = useBottomScrollListener(onBottom, { offset, debounce, debounceOptions, triggerOnNoScroll });
    
    if (typeof children === 'function') {
        return children(ref);
    }
    
    return children;
};
